/**
 * Merge Tags Utils
 * A set of utilities for working with merge tags in strings.
 *
 * Example usage:
  	const template = `
  		Hello {{user.name}}, you have {{fn(return state.variables.user?.subscriptions?.length;)}} subscription that
		is {{user.subscriptions->find:plan:eq:premium->details.value}}.
	";

	const variables = {
		user: {
		name: "Alice",
		subscriptions: [
			{ plan: "free", details: "Basic access" },
			{ plan: "premium", details: "Full access with premium features" }
		],
		emails: [{ address: "alice@example.com" }]
		}
	};
 */

import { Subject } from "rxjs";
import { MergeTagDto } from '../../creative-unit-package/models/merge-tag/merge-tag.dto';
import { ScriptDto } from '../../creative-unit-package/models/dto/script.dto';

/**
 * A map of function names to actual callable functions.
 *
 * Example usage:
 * const helperFns: MergeTagHelperFunctions = {
 *   greet: (name: string) => `Hello, ${name}!`,
 *   add: (a: number, b: number) => a + b,
 * };
 */
export interface MergeTagHelperFunctions {
	[fnName: string]: (...args: any[]) => any;
}

/**
 * Context for script execution, including available scripts
 */
export interface ScriptExecutionContext {
	scripts?: ScriptDto[];
	helperFns?: MergeTagHelperFunctions;
}

export interface MergeTagEvaluationError {
	expression: string;
	state: any;
	errorMessage: string;
}

export const mergeTagEvaluationErrors$ = new Subject<MergeTagEvaluationError>();

export class MergeTags {

	/**
	 * Applies merge tags to a template string using the provided variables.
	 *
	 * @param {string} templateString - The template string to apply merge tags to.
	 * @param {object} variables - The variables to use for replacing merge tags.
	 * @param {MergeTagHelperFunctions | ScriptExecutionContext} helperFnsOrContext - (Optional) Helper functions or full execution context with scripts.
	 * @returns {string} The template string with merge tags replaced by their corresponding values.
	 */
	public static applyMergeTagsToString(
		templateString: string,
		variables: any,
		helperFnsOrContext: MergeTagHelperFunctions | ScriptExecutionContext = {
			add(a: number, b: number) {
				return a + b;
			},
			greet(name: string) {
				// We can also call `this.add(10,20)` if we want,
				// but only if we rebind "this" (explained further below).
				// For now, let's keep it simple and not rely on "this":
				return `Hello, ${name}! Let's add 1+2 => ${this.add(1,2)}`;
			}
		}
	): string {
		// Normalize the parameter to always have a context
		let context: ScriptExecutionContext;
		if ('scripts' in helperFnsOrContext || 'helperFns' in helperFnsOrContext) {
			context = helperFnsOrContext as ScriptExecutionContext;
		} else {
			context = { helperFns: helperFnsOrContext as MergeTagHelperFunctions };
		}

		// Check if scripts exist in the variables (state) object
		// This allows scripts to flow through the state without explicit passing
		if (!context.scripts && variables?.scripts) {
			context.scripts = variables.scripts;
		}

		const helperFns = context.helperFns || {};

		// console.log("templateString final:", templateString);

		// Updated regex to also match @scriptName() syntax
		return templateString?.replace(
		/\{\{\s*(?:fn\s*\(\s*([\s\S]*?)\s*\)|@(\w+)\s*\((.*?)\)|([\w\s\/\-\>\:\._]+?)(?:\s*\|\|\s*'(.*?)')?)\s*\}\}/g,
		(match, fn, scriptName, scriptArgs, path, defaultValue) => {
			let value;

			if (fn) {
				Object.keys(helperFns).forEach(fnName => {
					helperFns[fnName] = helperFns[fnName].bind(helperFns);
				});

				// Use the updated evaluateExpression that accepts helperFns
				try {
					return this.evaluateExpression(fn, variables, helperFns, context.scripts);
				} catch (e) {
					// If there's an error, pass back useful debug info via the Subject
					const errorInfo: MergeTagEvaluationError = {
						expression: fn,
						state: variables,
						errorMessage: e?.message
					};

					console.error('Error evaluating merge tag expression:', errorInfo);
					mergeTagEvaluationErrors$.next(errorInfo);

					return '';
				}
			} else if (scriptName) {
				// Handle @scriptName() syntax
				return this.executeScript(scriptName, scriptArgs, variables, context.scripts, helperFns);
			} else {
				value = this.getValueByPath(variables, path);
				if (value === undefined || value === null || value === '') {
					value = defaultValue || '';
				}
				if (typeof value === 'string') {
					try {
						// sometimes values come surrounded with escaped quotes
						// this causes a bug when querying the datasource
						// removing the quotes fixes the bug
						// related to https://github.com/VMLYR/vyc-modular-video-builder/issues/6690
						if (value.startsWith('\\"') && value.endsWith('\\"')) {
							value = value.substring(2, value.length - 2);
						}

						// Escape value for JSON context
						return JSON.stringify(value).slice(1, -1);
					} catch {
						return value;
					}
				}
				return value;
			}
		}
		);
	}


	public static createMergeTag(value: string, index?: number): MergeTagDto {
		const currentDate = new Date().getTime().toString();
		// Recognize both {{}} syntax and @scriptName() pattern as chips
		const isChip = (value?.includes('{{') && value?.includes('}}'));
		const isScript = value?.includes('{{') && value?.includes('@') && value?.includes('(') && value?.includes(')');

		return {
			id: index >= 0 ? currentDate + '-' + index : currentDate,
			label: isChip ? value : null,
			value: value,
			isChip: isChip,
			isScript: isScript // Add flag to identify script merge tags
		} as MergeTagDto;
	}

	public static stringToMergeTags(value: string, scripts?: ScriptDto[]): MergeTagDto[] {
		if (!value) {
			return [];
		}

		return value
			.split(/(\{\{.*?}})/)
			.filter(part => part !== '')
			.map((part: string, index) => {
				return this.createMergeTag(part, index);
			});
	}

	/**
	 * Extracts the script name from a script merge tag
	 * @param mergeTag - The merge tag string (e.g., "{{@scriptName(params)}}")
	 * @returns The script name or null if not a script tag
	 */
	public static extractScriptName(mergeTag: string): string | null {
		const scriptMatch = mergeTag.match(/\{\{\s*@(\w+)\s*\(/);
		return scriptMatch ? scriptMatch[1] : null;
	}

	/**
	 * Validates JavaScript syntax for a script
	 * @param code - The JavaScript code to validate
	 * @returns An object with isValid boolean and optional error message
	 */
	public static validateScriptSyntax(code: string): { isValid: boolean; error?: string } {
		try {
			// Try to create a function with the code
			// Wrap in a function to validate the syntax
			new Function('state', code);
			return { isValid: true };
		} catch (error) {
			return {
				isValid: false,
				error: error instanceof Error ? error.message : 'Invalid JavaScript syntax'
			};
		}
	}

	/**
	 * Execute a script by its method name
	 * @param scriptName - The method name of the script to execute
	 * @param argsString - The arguments passed to the script as a string
	 * @param state - The current merge tag state/variables
	 * @param scripts - Available scripts
	 * @param helperFns - Helper functions available to the script
	 */
	private static executeScript(
		scriptName: string,
		argsString: string,
		state: any,
		scripts: ScriptDto[] | undefined,
		helperFns: MergeTagHelperFunctions
	): any {
		if (!scripts || scripts.length === 0) {
			console.error(`No scripts available for execution of @${scriptName}`);
			return '';
		}

		const script = scripts.find(s => s.methodName === scriptName);
		if (!script) {
			console.error(`Script with method name '${scriptName}' not found`);
			return '';
		}

		try {
			// Parse arguments - handle both positional and named parameters
			const args = this.parseScriptArguments(argsString, state);

			// Create a wrapper function that includes the script code
			// The script has access to state, args, and all helper functions
			const scriptFunction = `
				return (function(${args.names.join(', ')}) {
					${script.code}
				})(${args.values.map(v => JSON.stringify(v)).join(', ')});
			`;

			// Execute the script with the same context as fn() expressions
			return this.evaluateExpression(scriptFunction, state, helperFns, scripts);
		} catch (error) {
			console.error(`Error executing script '${scriptName}':`, error);
			return '';
		}
	}

	/**
	 * Parse script arguments from a string
	 * Supports both positional arguments: @script(arg1, arg2)
	 * And named arguments: @script(name: 'value', count: 5)
	 */
	private static parseScriptArguments(argsString: string, state: any): { names: string[], values: any[] } {
		const names: string[] = [];
		const values: any[] = [];

		if (!argsString || argsString.trim() === '') {
			return { names, values };
		}

		// Split arguments by comma, but respect nested parentheses and quotes
		const args = this.splitArguments(argsString);

		args.forEach((arg, index) => {
			const trimmedArg = arg.trim();

			// Check if it's a named parameter (contains ':')
			const colonIndex = trimmedArg.indexOf(':');
			if (colonIndex > 0 && colonIndex < trimmedArg.length - 1) {
				// Named parameter
				const name = trimmedArg.substring(0, colonIndex).trim();
				const valueStr = trimmedArg.substring(colonIndex + 1).trim();
				names.push(name);
				values.push(this.evaluateArgumentValue(valueStr, state));
			} else {
				// Positional parameter
				names.push(`arg${index}`);
				values.push(this.evaluateArgumentValue(trimmedArg, state));
			}
		});

		return { names, values };
	}

	/**
	 * Split arguments string by comma, respecting quotes and parentheses
	 */
	private static splitArguments(argsString: string): string[] {
		const args: string[] = [];
		let current = '';
		let depth = 0;
		let inSingleQuote = false;
		let inDoubleQuote = false;

		for (let i = 0; i < argsString.length; i++) {
			const char = argsString[i];
			const prevChar = i > 0 ? argsString[i - 1] : '';

			if (char === "'" && prevChar !== '\\' && !inDoubleQuote) {
				inSingleQuote = !inSingleQuote;
			} else if (char === '"' && prevChar !== '\\' && !inSingleQuote) {
				inDoubleQuote = !inDoubleQuote;
			} else if (!inSingleQuote && !inDoubleQuote) {
				if (char === '(' || char === '[' || char === '{') {
					depth++;
				} else if (char === ')' || char === ']' || char === '}') {
					depth--;
				} else if (char === ',' && depth === 0) {
					args.push(current);
					current = '';
					continue;
				}
			}
			current += char;
		}

		if (current) {
			args.push(current);
		}

		return args;
	}

	/**
	 * Evaluate an argument value - could be a literal, variable reference, or expression
	 */
	private static evaluateArgumentValue(valueStr: string, state: any): any {
		const trimmed = valueStr.trim();

		// Check for string literals
		if ((trimmed.startsWith('"') && trimmed.endsWith('"')) ||
			(trimmed.startsWith("'") && trimmed.endsWith("'"))) {
			return trimmed.slice(1, -1);
		}

		// Check for boolean/null literals
		if (trimmed === 'true') return true;
		if (trimmed === 'false') return false;
		if (trimmed === 'null') return null;
		if (trimmed === 'undefined') return undefined;

		// Check for numbers
		const num = Number(trimmed);
		if (!isNaN(num)) {
			return num;
		}

		// Otherwise, treat it as a path to evaluate from state
		return this.getValueByPath(state, trimmed);
	}

	/**
	 * Evaluate the fn(...) body with both `state` and the `helperFns`.
	 * We'll pass in each helper function as a separate parameter to new Function
	 * so the user can do:  {{ fn(return greet('Alice')) }}
	 */
	private static evaluateExpression(
	expression: string,
	state: any,
	helperFns: MergeTagHelperFunctions,
	scripts?: ScriptDto[]
	): any {
		try {
			// We want to create a dynamic function signature like:
			//   (state, add, greet, ...) => { <expression> }
			// so the user can call `greet(...)` as a top-level identifier.

			// Add scripts as helper functions so they can be called from within fn() expressions
			const enhancedHelperFns = { ...helperFns };
			if (scripts) {
				scripts.forEach(script => {
					enhancedHelperFns[script.methodName] = (...args: any[]) => {
						// Create parameter names for the script function
						const paramNames = args.map((_, i) => `arg${i}`);
						const scriptFunction = `
							return (function(${paramNames.join(', ')}) {
								${script.code}
							})(${args.map(v => JSON.stringify(v)).join(', ')});
						`;
						// Recursively evaluate the script
						return this.evaluateExpression(scriptFunction, state, helperFns, scripts);
					};
				});
			}

			const fnNames = Object.keys(enhancedHelperFns);          // e.g. ['add', 'greet', 'scriptName']
			const fnValues = Object.values(enhancedHelperFns);       // e.g. [ [Function], [Function], [Function] ]

			// Our param names are always 'state', then each function name
			const paramNames = ['state', ...fnNames];
			// Our param values match that order
			const paramValues = [state, ...fnValues];

			// Unescape the expression fully before we run it.
			// console.log('Evaluating expression:', expression);
			expression = expression
				// turn literal \n into real newline
				.replace(/\\n/g, '\n')
				// turn literal \r into real carriage return
				.replace(/\\r/g, '\r')
				// turn literal \t into tab
				.replace(/\\t/g, '\t')
				// handle escaped quotes
				.replace(/\\"/g, '"')
				.replace(/\\'/g, "'")
				// finally, collapse remaining double backslashes (\\ => \)
				.replace(/\\\\/g, '\\');

			// Create the dynamic function
			const dynamicFunction = new Function(...paramNames, expression);

			// Now call the dynamic function with the actual arguments
			return dynamicFunction(...paramValues);
		} catch (error) {
			console.error('Error evaluating expression:', expression, error);
			return '';
		}
	}



	/**
	 * Retrieves the value from an object using a given path.
	 * The path can include dot notation for nested properties and '->' for array find operations.
	 * If the value is not found, undefined is returned.
	 *
	 * @param variables - The object to search for the value.
	 * @param path - The path to the desired value.
	 * @returns The value found at the specified path, or undefined if not found.
	 */
	private static getValueByPath(variables, path) {
		// Initialize the current object to the passed variables
		let current = variables;

		// Split the path on '->', which may include a 'find' operation or be a direct path
		let pathSegments = path.split('->');

		// Iterate over the segments to process each one
		for (let i = 0; i < pathSegments.length; i++) {
			let segment = pathSegments[i];
			// console.log('Segment:', segment, segment.startsWith('find'), i, pathSegments);

			// Handle the find operation
			if (segment.startsWith('find')) {
				//&& i < pathSegments.length - 1
				// console.log('Find operation:', segment);
				// Remove find: from the segment
				let searchQuery = segment.replace('find:', '');
				current = this.findInArray(current, searchQuery);

				// console.log('Found:', current, searchQuery);

				if (current === undefined) {
					// If nothing is found, return undefined immediately
					return undefined;
				}
			} else {
				// Split the segment into subparts in case of dot notation
				let keys = segment.split('.');
				for (let key of keys) {
					if (current?.[key] === undefined) {
						return undefined; // If the key is not found, return undefined
					}
					current = current[key];
				}
			}
		}
		return current;
	}

	/**
	 * Finds an item in an array based on a search query.
	 * @param array - The array to search in.
	 * @param searchQuery - The search query in the format "key:condition:value".
	 * @returns The found item or undefined if not found.
	 * @throws Error if the search condition is unsupported.
	 */
	private static findInArray(array, searchQuery) {
		// Split the search query into its components: key, condition, and value
		let [key, condition, searchValue] = searchQuery.split(':');
		// Based on the condition, perform the appropriate search
		switch (condition) {
			case 'eq': // Equals condition
				return array.find(item => item[key] === searchValue);
			case 'neq': // Not equals condition
				return array.find(item => item[key] !== searchValue);
			default:
				throw new Error(`Unsupported search condition: ${condition}`);
		}
	}
}
